import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://semperfipumps.com";
const SITE_NAME = "Semper Fi Pump Service";
const OG_IMAGE = `${SITE_URL}/og-image.jpg`;

interface SeoHeadProps {
  title: string;
  description: string;
  noindex?: boolean;
  /** Hint for prerenderers (e.g. 404). When set, also forces noindex. */
  statusCode?: number;
  /**
   * Optional absolute path to use as the canonical (e.g. "/services/well-pump-repair").
   * Use when this URL duplicates another page's content. Defaults to the current pathname.
   */
  canonicalPath?: string;
}

/**
 * Normalize a path for use in a canonical URL:
 * - lowercase
 * - strip query string and hash
 * - strip trailing slash (except for root "/")
 */
const normalizeCanonicalPath = (path: string): string => {
  // Drop search and hash
  const noQuery = path.split("?")[0].split("#")[0];
  const lower = noQuery.toLowerCase();
  if (lower === "/" || lower === "") return "/";
  return lower.replace(/\/+$/, "");
};

export const SeoHead = ({ title, description, noindex, statusCode, canonicalPath }: SeoHeadProps) => {
  const { pathname } = useLocation();
  const sourcePath = canonicalPath ?? pathname;
  const normalized = normalizeCanonicalPath(sourcePath);
  const canonical = `${SITE_URL}${normalized === "/" ? "" : normalized}`;
  const isNoindex = noindex || (statusCode !== undefined && statusCode >= 400);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {/* Only emit a canonical for indexable pages — never canonicalize a 404 / noindex page to itself. */}
      {!isNoindex && <link rel="canonical" href={canonical} />}
      {isNoindex && <meta name="robots" content="noindex, nofollow" />}
      {isNoindex && <meta name="googlebot" content="noindex, nofollow" />}
      {statusCode && <meta name="prerender-status-code" content={String(statusCode)} />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE} />
    </Helmet>
  );
};
